import { useMemo } from "react";
import { ActivityIndicator, Pressable, ScrollView } from "react-native";
import { Stack } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View, Text, Card } from "@src/components";
import { Checkbox } from "@src/components/Checkbox";
import {
  MonitoringBannerSection,
  SiteWideRecordingFooter,
  MonitoringDialogs,
} from "@src/features/alarms/components/AlarmResponseShared";
import { getCameraKeyString } from "@src/utils/key-parser";
import { PressableScale } from "pressto";
import { useManageAlarmResponse } from "./useManageAlarmResponse";

const TEXT_COLUMN_WIDTH = 230;
const CHECKBOX_COLUMN_WIDTH = 200;
const TOTAL_NUMBER_OF_COLUMNS = 3;
const TOTAL_NUMBER_OF_COLUMNS_WITHOUT_MONITORING = 2;

export default function ManageAlarmResponseScreen() {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();

  const {
    siteCameras,
    isLoading,
    effectiveResponses,
    toggleResponse,
    allSirensOn,
    allStrobesOn,
    allMonitoringOn,
    toggleAllSirens,
    toggleAllStrobes,
    toggleAllMonitoring,
    showMonitoring,
    hasChanges,
    isSaving,
    save,
    showUpgradeModal,
    setShowUpgradeModal,
    showRequestSentModal,
    setShowRequestSentModal,
    handleUpgradeRequest,
  } = useManageAlarmResponse();

  const containerWidth = useMemo(() => {
    return (
      TEXT_COLUMN_WIDTH +
      (showMonitoring ? TOTAL_NUMBER_OF_COLUMNS : TOTAL_NUMBER_OF_COLUMNS_WITHOUT_MONITORING) * CHECKBOX_COLUMN_WIDTH +
      theme.spacing["4xl"] * 2
    );
  }, [showMonitoring, theme.spacing]);

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <ScrollView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        contentContainerStyle={{
          paddingTop: theme.spacing["4xl"],
          paddingBottom: bottom + theme.spacing["6xl"],
          flexGrow: 1,
        }}
        showsVerticalScrollIndicator={false}
      >
        {isLoading && (
          <View mt="7xl" alignItems="center">
            <ActivityIndicator color={theme.colors.text} />
          </View>
        )}

        {!isLoading && (
          <View
            style={{
              maxWidth: containerWidth,
            }}
          >
            <View alignItems="flex-end" mb="xl">
              <Pressable
                onPress={save}
                style={{ opacity: !isSaving && hasChanges ? 1 : 0.5 }}
                disabled={isSaving || !hasChanges}
              >
                {isSaving ?
                  <ActivityIndicator size="small" />
                : <Text textStyle="md/semibold" color={hasChanges ? "navy" : "textMuted"}>
                    Save
                  </Text>
                }
              </Pressable>
            </View>
            <View mb="3xl">
              <MonitoringBannerSection showMonitoring={showMonitoring} onUpgrade={() => setShowUpgradeModal(true)} />
            </View>

            <Card>
              {/* Column headers */}
              <View style={localStyles.headerRow} px="none" gap="xxs">
                <View w={TEXT_COLUMN_WIDTH} />
                <View style={localStyles.columnHeader}>
                  <View style={localStyles.textHeader}>
                    <Text textStyle="xs/medium" color="textMuted">
                      Sirens
                    </Text>
                  </View>
                </View>
                <View style={localStyles.columnHeader}>
                  <View style={localStyles.textHeader}>
                    <Text textStyle="xs/medium" color="textMuted">
                      Strobes
                    </Text>
                  </View>
                </View>
                {showMonitoring && (
                  <View style={localStyles.columnHeader}>
                    <View style={localStyles.textHeader}>
                      <Text textStyle="xs/medium" color="textMuted">
                        Monitoring
                      </Text>
                    </View>
                  </View>
                )}
              </View>

              {/* Camera rows */}
              {siteCameras.map((camera, index) => {
                const key = getCameraKeyString(camera);
                const state = effectiveResponses.get(key) ?? { sirens: "off", strobes: "off", monitoring: "off" };
                const isLastItem = index === siteCameras.length - 1;

                return (
                  <View key={key} style={[localStyles.row, isLastItem && { borderBottomWidth: 0 }]} px="none" gap="xxs">
                    <View w={TEXT_COLUMN_WIDTH}>
                      <Text textStyle="sm/regular" color="text" numberOfLines={3}>
                        {key}
                      </Text>
                    </View>
                    <View style={localStyles.checkboxCell}>
                      <Checkbox checked={state.sirens === "on"} onToggle={() => toggleResponse(key, "sirens")} />
                    </View>
                    <View style={localStyles.checkboxCell}>
                      <Checkbox checked={state.strobes === "on"} onToggle={() => toggleResponse(key, "strobes")} />
                    </View>
                    {showMonitoring && (
                      <View style={localStyles.checkboxCell}>
                        <Checkbox
                          disabled={true}
                          checked={state.monitoring === "on"}
                          onToggle={() => toggleResponse(key, "monitoring")}
                        />
                      </View>
                    )}
                  </View>
                );
              })}

              {/* Select all buttons */}
              <View style={localStyles.selectAllRow} px="none" gap="xxs">
                <View w={TEXT_COLUMN_WIDTH} />
                <View style={localStyles.checkboxCell}>
                  <PressableScale onPress={toggleAllSirens} style={{ width: "90%" }}>
                    <View style={localStyles.outlinedButton}>
                      <Text textStyle="xs/regular">{allSirensOn ? "Deselect all" : "Select all"}</Text>
                    </View>
                  </PressableScale>
                </View>
                <View style={localStyles.checkboxCell}>
                  <PressableScale onPress={toggleAllStrobes} style={{ width: "90%" }}>
                    <View style={localStyles.outlinedButton}>
                      <Text textStyle="xs/regular">{allStrobesOn ? "Deselect all" : "Select all"}</Text>
                    </View>
                  </PressableScale>
                </View>
                {showMonitoring && (
                  <View style={localStyles.checkboxCell}>
                    <PressableScale
                      onPress={toggleAllMonitoring}
                      enabled={false}
                      style={{ opacity: 0.5, width: "90%" }}
                    >
                      <View style={localStyles.outlinedButton}>
                        <Text textStyle="xs/regular">{allMonitoringOn ? "Deselect all" : "Select all"}</Text>
                      </View>
                    </PressableScale>
                  </View>
                )}
              </View>
            </Card>

            <SiteWideRecordingFooter />
          </View>
        )}
      </ScrollView>

      <MonitoringDialogs
        showUpgradeModal={showUpgradeModal}
        setShowUpgradeModal={setShowUpgradeModal}
        showRequestSentModal={showRequestSentModal}
        setShowRequestSentModal={setShowRequestSentModal}
        handleUpgradeRequest={handleUpgradeRequest}
      />
    </>
  );
}

const localStyles = StyleSheet.create((theme) => ({
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["3xl"],
  },
  columnHeader: {
    width: CHECKBOX_COLUMN_WIDTH,
    alignItems: "center",
    gap: theme.spacing.lg,
  },
  textHeader: {
    width: "100%",
    alignItems: "center",
    backgroundColor: theme.colors.borderCard,
    paddingVertical: theme.spacing.md,
    borderTopLeftRadius: theme.spacing.xs,
    borderTopRightRadius: theme.spacing.xs,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["7xl"],
    borderBottomWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  checkboxCell: {
    width: CHECKBOX_COLUMN_WIDTH,
    alignItems: "center",
  },
  selectAllRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: theme.spacing["3xl"],
    marginTop: theme.spacing["3xl"],
  },
  outlinedButton: {
    borderWidth: 1,
    borderColor: theme.colors.interactive.buttons.default_background,
    borderRadius: theme.spacing.lg,
    paddingHorizontal: theme.spacing["3xl"],
    paddingVertical: theme.spacing.md,
    alignItems: "center",
    backgroundColor: theme.colors.foreground,
  },
}));
